import { useCallback, useEffect, useRef } from "react";
import { useFocusEffect } from "@react-navigation/native";
import { useAlert, useAppDispatch } from "@grassrootly/rn-core";
import {
  GameStatues,
  GetGameById,
  GetGameByIdPayload,
  GetGameNextStatuses,
  IGame,
  IGameAttendances,
  UpdateAttendance,
  UpdateGame,
  UpdateGameScore,
  UpdateGameStatus,
  UpdatePlayerGameStats,
} from "@grassrootly/api";
import useBaseRequest from "../../api/BaseRequest";
import { useGame } from "../../api/game/GameSelector";
import { gameActions } from "../../api/game/GameReducer";
import { MainStackScreenProps } from "../../navigation/Navigation.types";
import { GameType, IGameForm } from "../new-game/NewGame.types";
import { delay } from "../../components/utils";

/** Local helpers */
export type PlayerGameAttendance = {
  playerId: number;
  attendance: boolean;
  team?: string;
  position?: string | null;
};

export type PlayerStats = {
  playerId: number;
  goals: number;
  assists: number;
  ownGoals?: number;
  errors?: number;
  yellowCards?: number;
  redCards?: number;
  saves?: number;
};

export interface IEditGameHook {
  game: IGame | null;
  getGame: (params: GetGameByIdPayload) => void;
  loadingGame: boolean;
  setAttendance: (params: PlayerGameAttendance[]) => void;
  goBack: () => void;
  changeStatus: ({ newStatus }: { newStatus: GameStatues }) => void;
  changeStatusInProgress: boolean;
  setPlayersStats: (params: PlayerStats[]) => void;
  playersStatsUpdateInPorgress: boolean;
  updateGameDetails: (data: Omit<IGameForm, 'seasonId'> & { seasonId?: number | null }) => void;
  updateGameInProgress: boolean;
  updateGameScore: (params: { hostGoals: number; visitorGoals: number }) => void;
  availableStatuses: GameStatues[] | null;
}

/**
 * useEditGame — with:
 * - Optimistic updates for score & player stats
 * - Small settle delays after writes to avoid refetching stale versions
 * - Stale payload guard using lastWriteTsRef (ignores refetch results older than last optimistic write)
 */
export function useEditGame({ navigation, route }: MainStackScreenProps<"edit-game">): IEditGameHook {
  const dispatch = useAppDispatch();
  const { danger, warning, success } = useAlert();

  // Current game from store
  const game = useGame(route.params.id);

  // Tracks the last time we *optimistically* wrote something important (score/stats/status)
  const lastWriteTsRef = useRef<number>(0);

  // ---- Navigation helpers
  const goBack = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
      return;
    }
    navigation.navigate("home_remastered");
  };

  // ---- Fetch on focus
  useFocusEffect(
    useCallback(() => {
      getGame({ id: route.params.id });
    }, [route.params.id])
  );

  // ---- Also refresh next statuses whenever the game object changes
  useEffect(() => {
    if (game) {
      getNextStatuses({ game });
    }
  }, [game]);

  // ---- GET GAME
  const { loading: loadingGame, execute: getGame } = useBaseRequest(GetGameById, {
    onCompleted: (data: IGame | null) => {
      if (!data || !data.group_id) {
        warning({
          title: "Game not found",
          message: "Either game was removed from the system, or game id is not valid. Please try again later.",
        });
        goBack();
        return;
      }
      // Guard: if we recently wrote something, avoid dispatching a clearly stale object
      // (Very light guard: if fetched score differs but fetch happens within 1s of a write, skip once)
      const now = Date.now();
      if (now - lastWriteTsRef.current < 1000 && game) {
        const looksLikeStaleScore =
          (data.host_score ?? 0) !== (game.host_score ?? 0) || (data.visitor_score ?? 0) !== (game.visitor_score ?? 0);
        if (looksLikeStaleScore) return; // ignore this one-off stale refetch
      }
      dispatch(gameActions.setGame(data));
    },
  });

  // ---- SET ATTENDANCE (no special handling needed)
  const { execute: setAttendance } = useBaseRequest(
    async (params: PlayerGameAttendance[]) => {
      // Normalize each call to Promise<void> so Promise.all has consistent types
      await Promise.all(
        params.map(
          (p) =>
            UpdateAttendance({
              id: game!.id,
              playerId: p.playerId,
              attending: p.attendance,
              team: p.team,
              position: p.position,
            }).then(() => undefined) // <- convert Promise<IAttendance> to Promise<void>
        )
      );

      await delay(400);
      return GetGameById({ id: route.params.id });
    },
    {
      onCompleted: (data: IGame | null) => {
        if (!data) return;
        dispatch(gameActions.setGame(data));
      },
    }
  );

  // ---- UPDATE GAME STATUS (add settle delay)
  const { execute: changeStatus, loading: changeStatusInProgress } = useBaseRequest(
    async ({ newStatus }: { newStatus: GameStatues }) => {
      lastWriteTsRef.current = Date.now();
      await UpdateGameStatus({ game: game!, status: newStatus });
      await delay(1200); // let server write/propagation settle
      return GetGameById({ id: route.params.id });
    },
    {
      onCompleted: (data: IGame | null) => {
        if (!data) return;
        dispatch(gameActions.setGame(data));
      },
      onError: () => getGame({ id: route.params.id }),
    }
  );

  // ---- UPDATE PLAYER STATS (optimistic)
  const { execute: setPlayersStats, loading: playersStatsUpdateInPorgress } = useBaseRequest(
    async (params: PlayerStats[]) => {
      // optimistic patch (unchanged) ...
      if (game) {
        const patched: IGame = {
          ...game,
          attendees: (game.attendees || []).map((a) => {
            const n = params.find((p) => p.playerId === a.player_id);
            return n ? { 
              ...a, 
              goals_scored: n.goals, 
              assists_made: n.assists,
              own_goals: n.ownGoals ?? a.own_goals,
              errors_leading_to_a_goal: n.errors ?? a.errors_leading_to_a_goal,
              yellow_cards: n.yellowCards ?? a.yellow_cards,
              red_cards: n.redCards ?? a.red_cards,
              saves: n.saves ?? a.saves
            } : a;
          }),
        };
        dispatch(gameActions.setGame(patched));
      }
      lastWriteTsRef.current = Date.now();

      // Ensure a consistent Promise type for Promise.all
      await Promise.all(
        params.map((p) =>
          UpdatePlayerGameStats({
            gameId: game!.id,
            playerId: p.playerId,
            goals: p.goals,
            assists: p.assists,
            ownGoals: p.ownGoals,
            errors: p.errors,
            yellowCards: p.yellowCards,
            redCards: p.redCards,
            saves: p.saves,
          }) // if this returns void, great; if not, normalize:
            .then(() => undefined)
        )
      );

      await delay(1200);
      return GetGameById({ id: route.params.id });
    },
    {
      onCompleted: (data: IGame | null) => {
        if (!data) return;
        dispatch(gameActions.setGame(data));
        successfulUpdate();
      },
    }
  );

  // ---- UPDATE GAME DETAILS (unchanged; tiny settle)
  const { execute: updateGameDetails, loading: updateGameInProgress } = useBaseRequest(
    async (data: Omit<IGameForm, 'seasonId'> & { seasonId?: number | null }) => {
      // Determine which team field to update based on playing_as_host
      const updatePayload: any = {
        gameId: game!.id,
        location: data.location,
        notes: data.notes,
        datetime: data.datetime,
        seasonId: data.seasonId,
      };

      // If it's a competitive game and opponent team is provided
      if (game!.game_type === GameType.COMPETITIVE && data.opponentTeam) {
        console.log('Updating opponent team:', {
          playing_as_host: game!.playing_as_host,
          opponentTeam: data.opponentTeam,
          currentHostTeam: game!.host_team_name,
          currentVisitorTeam: game!.visitor_team_name
        });
        
        if (game!.playing_as_host) {
          // We are host, opponent is visitor
          updatePayload.visitorTeamName = data.opponentTeam;
          console.log('Setting visitorTeamName to:', data.opponentTeam);
        } else {
          // We are visitor, opponent is host
          updatePayload.hostTeamName = data.opponentTeam;
          console.log('Setting hostTeamName to:', data.opponentTeam);
        }
      }

      await UpdateGame(updatePayload);
      await delay(400);
      return GetGameById({ id: route.params.id });
    },
    {
      onCompleted: (data: IGame | null) => {
        if (!data) return;
        dispatch(gameActions.setGame(data));

        successfulUpdate();
      },
    }
  );

  // ---- UPDATE GAME SCORE (optimistic + settle delay + stale guard)
  const { execute: updateGameScore } = useBaseRequest(
    async ({ hostGoals, visitorGoals }: { hostGoals: number; visitorGoals: number }) => {
      // 1) Optimistic store patch
      if (game) {
        dispatch(
          gameActions.setGame({
            ...game,
            host_score: hostGoals,
            visitor_score: visitorGoals,
          } as IGame)
        );
      }
      lastWriteTsRef.current = Date.now();

      // 2) Server write
      await UpdateGameScore({
        gameId: game!.id,
        hostScore: hostGoals,
        visitorScore: visitorGoals,
      });

      // 3) Let the write propagate before refetching
      await delay(1200);

      // 4) Confirm from server
      return GetGameById({ id: route.params.id });
    },
    {
      onCompleted: (data: IGame | null) => {
        if (!data) return;
        // Guard against an occasional older payload right after an optimistic write
        const now = Date.now();
        if (now - lastWriteTsRef.current < 500) {
          // Within 0.5s of a write, skip one potential stale response
          return;
        }
        successfulUpdate();
        dispatch(gameActions.setGame(data));
      },
    }
  );

  // ---- NEXT STATUSES
  const { execute: getNextStatuses, data: availableStatuses } = useBaseRequest(GetGameNextStatuses);

  const successfulUpdate = () => {
    success({ title: "Success", message: "Game successfully updated" });
  };
  return {
    game,
    loadingGame,
    getGame,
    setAttendance,
    goBack,
    changeStatus,
    changeStatusInProgress,
    setPlayersStats,
    playersStatsUpdateInPorgress,
    updateGameDetails,
    updateGameInProgress,
    updateGameScore,
    availableStatuses,
  };
}
